import * as yup from 'yup'
import { IntlShape, defineMessages } from 'react-intl'
import { IBusinessCustomerFormValues } from 'features/BusinessCustomer/types'
import schemaValidations from 'utils/schema'
import { isEmpty } from 'lodash'
import { LATITUDE_REGEX_STRING, LONGITUDE_REGEX_STRING } from 'utils/google-maps'

const messages = defineMessages({
  required: {
    id: 'businessCustomer.form.required',
    defaultMessage: 'This field is required'
  },
  max: {
    id: 'businessCustomer.form.max',
    defaultMessage: 'This field must be at most {max} characters'
  },
  phoneLength: {
    id: 'businessCustomer.form.phoneMin',
    defaultMessage: 'Enter {nDigits} digits'
  }
})

const latitudeMessages = defineMessages({
  required: {
    id: 'places.new.form.latitude.required',
    defaultMessage: 'The latitude is required'
  },
  format: {
    id: 'places.new.form.latitude.format',
    defaultMessage: 'The latitude format is wrong'
  }
})

const longitudeMessages = defineMessages({
  required: {
    id: 'places.new.form.longitude.required',
    defaultMessage: 'The longitude is required'
  },
  format: {
    id: 'places.new.form.longitude.format',
    defaultMessage: 'The longitude format is wrong'
  }
})

export const schema = (intl: IntlShape): yup.SchemaOf<IBusinessCustomerFormValues> => {
  return yup.object().shape(
    {
      kreditsSettings: yup.object().shape({
        amount: yup.string(),
        category: yup.string()
      }) as yup.SchemaOf<IBusinessCustomerFormValues['kreditsSettings']>,
      userAccountId: yup.string(),
      customerType: yup.string().required(intl.formatMessage(messages.required)) as yup.SchemaOf<
        IBusinessCustomerFormValues['customerType']
      >,
      firstName: yup
        .string()
        .max(200, intl.formatMessage(messages.max, { max: 200 }))
        .when('placeAddress', {
          is: (placeAddress: IBusinessCustomerFormValues['placeAddress']) => !isEmpty(placeAddress?.street),
          then: yup
            .string()
            .max(200, intl.formatMessage(messages.max, { max: 200 }))
            .required(intl.formatMessage(messages.required))
        }),
      lastName: yup
        .string()
        .max(200, intl.formatMessage(messages.max, { max: 200 }))
        .when('placeAddress', {
          is: (placeAddress: IBusinessCustomerFormValues['placeAddress']) => !isEmpty(placeAddress?.street),
          then: yup
            .string()
            .max(200, intl.formatMessage(messages.max, { max: 200 }))
            .required(intl.formatMessage(messages.required))
        }),
      email: schemaValidations(intl).email.optional.when('phoneNumber', {
        is: (phoneNumber: IBusinessCustomerFormValues['phoneNumber']): boolean => isEmpty(phoneNumber.number),
        then: schemaValidations(intl).email.required
      }),
      phoneNumber: yup
        .object()
        .shape({
          number: schemaValidations(intl).phoneNumber.optional,
          countryCode: yup.string()
        })
        .when('email', {
          is: (email: string): boolean => {
            return isEmpty(email)
          },
          then: yup.object().shape({
            number: schemaValidations(intl).phoneNumber.required,
            countryCode: yup.string()
          })
        }),
      businessClient: yup.object().shape({
        id: yup.string().required(intl.formatMessage(messages.required)),
        name: yup.string(),
        verticals: yup.array().of(yup.string())
      }) as yup.SchemaOf<IBusinessCustomerFormValues['businessClient']>,
      businessSite: yup.object().shape({
        id: yup.string(),
        name: yup.string()
      }) as yup.SchemaOf<IBusinessCustomerFormValues['businessSite']>,
      placeAddress: yup.object().shape(
        {
          cityId: yup.string(),
          street: yup.string().when(['cityName', 'zipCode'], {
            is: (cityName: string, zipCode: string) => !isEmpty(cityName) || !isEmpty(zipCode),
            then: yup.string().required(intl.formatMessage(messages.required))
          }),
          cityName: yup.string().when(['street'], {
            is: (street: string) => !isEmpty(street),
            then: yup
              .string()
              .max(200, intl.formatMessage(messages.max, { max: 200 }))
              .required(intl.formatMessage(messages.required))
          }),
          zipCode: yup.string().when(['street'], {
            is: (cityName: string) => !isEmpty(cityName),
            then: schemaValidations(intl).address.zipCode.required
          }),
          location: yup.object().shape(
            {
              latitude: yup
                .string()
                .matches(new RegExp(LATITUDE_REGEX_STRING), intl.formatMessage(latitudeMessages.format))
                .when('longitude', {
                  is: (longitude: string) => !isEmpty(longitude),
                  then: yup.string().required(intl.formatMessage(latitudeMessages.required))
                }),
              longitude: yup
                .string()
                .matches(new RegExp(LONGITUDE_REGEX_STRING), intl.formatMessage(longitudeMessages.format))
                .when('latitude', {
                  is: (latitude: string) => !isEmpty(latitude),
                  then: yup.string().required(intl.formatMessage(longitudeMessages.required))
                })
            },
            [['latitude', 'longitude']]
          )
        },
        [
          ['street', 'cityName'],
          ['zipCode', 'street']
        ]
      ) as yup.SchemaOf<IBusinessCustomerFormValues['placeAddress']>
    },
    [['phoneNumber', 'email']]
  )
}
